import { firestore } from "firebase/app";
import { RecordInfo } from "./RecordInfo";
import ProjectOverview from "./ProjectOverview";
import { joinToString } from "@/utils/LocalisationHelper";
import { DamagePatternConclusionState } from "./DryingProjectOverview";
import { isLeakDetectionProject as getIsLeakDetectionProject } from "@/data/repository/ProjectRepository";
import Vue from "vue";
import { ProjectManagement } from "./Client";
import { GeoLocation } from "@/utils/GeocodingService";
import { getProjectManagementType } from "../repository/ClientRepository";

export const FIRST_CONTACT_PERSON_ID = "FIRST_CONTACT_PERSON_ID";
export const FIRST_ROOM_ID = "FIRST_ROOM_ID";
export const USED_FOR_ELECTRICITY_BILLING_PREFIX = "ELECTRICITY_BILLING_";

/**
 * Electricity-billing apartments ("Strombezug") are numbered by the project app in their own
 * namespace, offset by this value: `apartmentNumber` 1001 is "Strombezug 1", 1002 is "Strombezug 2".
 * The offset keeps them from colliding with real apartment numbers ("Wohnung 1", "Wohnung 2", …),
 * which share the `contactPersonNumber` space.
 */
export const ELECTRICITY_BILLING_APARTMENT_NUMBER_OFFSET = 1000;

export enum ProjectOperationName {
  ADDED_LAST_TIME_CHANGED = "ADDED_LAST_TIME_CHANGED",
  ARCHIVE_DELETED_PROJECT = "ARCHIVE_DELETED_PROJECT",
  PROJECT_APPROVED_BY_USER = "PROJECT_APPROVED_BY_USER",
  PROJECT_AUTOMATICALLY_DELETED = "PROJECT_AUTOMATICALLY_DELETED",
  PROJECT_CREATED_BY_USER = "PROJECT_CREATED_BY_USER",
  PROJECT_DELETED_BY_USER = "PROJECT_DELETED_BY_USER",
  PROJECT_EDITED_BY_USER = "PROJECT_EDITED_BY_USER",
  PROJECT_FINISHED_BY_USER = "PROJECT_FINISHED_BY_USER",
  PROJECT_MOVED_TO_ARCHIVED_PROJECTS = "PROJECT_MOVED_TO_ARCHIVED_PROJECTS",
  PROJECT_MOVED_TO_FINISHED_PROJECTS = "PROJECT_MOVED_TO_FINISHED_PROJECTS",
  PROJECT_OWNERS_UPDATED = "PROJECT_OWNERS_UPDATED",
  PROJECT_REACTIVATED_BY_API = "PROJECT_REACTIVATED_BY_API",
  PROJECT_REACTIVATED_BY_USER = "PROJECT_REACTIVATED_BY_USER",
  PROJECT_REJECTED_BY_USER = "PROJECT_REJECTED_BY_USER",
  PROJECT_REOPENED_BY_API = "PROJECT_REOPENED_BY_API",
  PROJECT_REOPENED_BY_USER = "PROJECT_REOPENED_BY_USER",
  PROJECT_RESTORED_BY_USER = "PROJECT_RESTORED_BY_USER",
  UPDATED_LAST_TIME_CHANGED = "UPDATED_LAST_TIME_CHANGED",
}

export enum ProjectConclusionState {
  ARCHIVED = "ARCHIVED",
  DELETED = "DELETED",
  FINISHED = "FINISHED",
  MOVED_TO_FINSIHED = "MOVED_TO_FINISHED",
  RESTORED = "RESTORED",
}

export enum ProjectOperationReason {
  API_ACTION = "API_ACTION",
  FINISHED_PROJECT_EXCEEDS_AGE_THRESHOLD = "FINISHED_PROJECT_EXCEEDS_AGE_THRESHOLD",
  ON_HOLD_PROJECT_EXCEEDS_AGE_THRESHOLD = "ON_HOLD_PROJECT_EXCEEDS_AGE_THRESHOLD",
  PROJECT_FINALISATION_MISSING_IN_K4 = "PROJECT_FINALISATION_MISSING_IN_K4",
  PROJECT_FINALISATION_MISSING_IN_K8 = "PROJECT_FINALISATION_MISSING_IN_K8",
  PROJECT_INACTIVE = "PROJECT_INACTIVE",
  PROJECT_DELETED_IN_APP = "PROJECT_DELETED_IN_APP",
  USER_ACTION = "USER_ACTION",
}

export enum ProjectOperationSource {
  API = "API",
  PORTAL = "PORTAL",
  PROJECT_MANAGER_APP = "PROJECT_MANAGER_APP",
  DEVICE_MANAGER_APP = "DEVICE_MANAGER_APP",
  CLOUD_FUNCTION = "CLOUD_FUNCTION",
  ERP = "ERP",
  PYTHON_SCRIPT = "PYTHON_SCRIPT",
}

enum ProjectType {
  LEAK_DETECTION = "LEAK_DETECTION",
  DRYING = "DRYING",
}

export enum ExtendedProjectType {
  LEAK_DETECTION = "LEAK_DETECTION",
  DRYING = "DRYING",
  DAMAGE_ASSESSMENT_DRYING = "DAMAGE_ASSESSMENT_DRYING",
  DAMAGE_ASSESSMENT_DRYING_RESTORATION = "DAMAGE_ASSESSMENT_DRYING_RESTORATION",
  DAMAGE_ASSESSMENT_FIRE = "DAMAGE_ASSESSMENT_FIRE",
  DAMAGE_ASSESSMENT_RESTORATION = "DAMAGE_ASSESSMENT_RESTORATION",
  RESTORATION = "RESTORATION",
  DEVICE_ASSIGNMENT_DRYING = "DEVICE_ASSIGNMENT_DRYING",
  DEVICE_ASSIGNMENT_LENDING = "DEVICE_ASSIGNMENT_LENDING",
}

export enum VisibilityRestriction {
  WEBSITE_ONLY = "WEBSITE_ONLY",
}

export enum ExtendedVisibilityRestriction {
  WEBSITE_ONLY = "WEBSITE_ONLY",
  PROJECT_APP_ONLY = "PROJECT_APP_ONLY",
  WORK_APP_ONLY = "WORK_APP_ONLY",
}

export interface ProjectTypeConfig {
  projectTypeId: number;
  active: boolean;
  projectScope: ExtendedProjectType[];
  platforms: ProjectOperationSource[];
  label?: string | null;
  typeBadge?: string | null;
}

export interface ProjectLastOperation {
  name: ProjectOperationName;
  timestamp: firestore.Timestamp;
  recordedByUserId: string;
  reason?: ProjectOperationReason;
  source?: ProjectOperationSource;
}

export interface ProjectConclusion {
  conclusionState: ProjectConclusionState;
  conclusionDate: firestore.Timestamp;
}

export const KEY_ROOM_USAGE = "Raum0020";
export const KEY_ROOM_NAME = "Raum0025";

/**
 * Canonical registry of all supported project types.
 *
 * Notes:
 *  - `projectTypeId` is the stable identifier used across API, UI, and persistence.
 *  - `projectScope` enumerates the fine-grained capabilities (see {@link ExtendedProjectType}).
 *  - `platforms` enumerates where the type can be operated (see {@link ProjectOperationSource}).
 *  - `label` and `typeBadge` are optional client overrides; defaults come from i18n when not provided.
 *  - `active` here represents the default for a cold start; client-specific activation is computed
 *    via {@link getDefaultProjectTypes} and saved in settings, if edited.
 *
 * Maintenance:
 *  - When adding a new type, also review:
 *      • Legacy resolvers: {@link getLegacyLeakDetectionProjectTypeId}, {@link getLegacyDryingProjectTypeId},
 *        {@link getLegacyLeakDetectionAndDryingProjectTypeId}, {@link getLegacyPortalOnlyProjectTypeId}.
 *      • Defaults logic: {@link getDefaultProjectTypes} (activation and platform pruning).
 *  - Ensure `projectScope` and `platforms` reflect the intended behavior in Project/Portal/Work apps.
 */
export const ALL_PROJECT_TYPES: ProjectTypeConfig[] = [
  { 
    projectTypeId: 1, 
    active: false, 
    label: null,  
    projectScope: [
      ExtendedProjectType.LEAK_DETECTION
    ], 
    platforms: [
      ProjectOperationSource.PROJECT_MANAGER_APP
    ] 
  },
  { 
    projectTypeId: 2, 
    active: false, 
    label: null,  
    projectScope: [
      ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING, 
      ExtendedProjectType.DRYING
    ], 
    platforms: [
      ProjectOperationSource.PROJECT_MANAGER_APP
    ] 
  },
  { 
    projectTypeId: 3, 
    active: false, 
    label: null,  
    projectScope: [
      ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING_RESTORATION, 
      ExtendedProjectType.DRYING, 
      ExtendedProjectType.RESTORATION
    ], 
    platforms: [
      ProjectOperationSource.PROJECT_MANAGER_APP
    ] 
  },
  { 
    projectTypeId: 4, 
    active: false, 
    label: null,  
    projectScope: [
      ExtendedProjectType.LEAK_DETECTION, 
      ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING, 
      ExtendedProjectType.DRYING
    ], 
    platforms: [
      ProjectOperationSource.PROJECT_MANAGER_APP
    ] 
  },
  { 
    projectTypeId: 5, 
    active: false, 
    label: null,  
    projectScope: [
      ExtendedProjectType.LEAK_DETECTION, 
      ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING_RESTORATION, 
      ExtendedProjectType.DRYING, 
      ExtendedProjectType.RESTORATION
    ], 
    platforms: [
      ProjectOperationSource.PROJECT_MANAGER_APP
    ] 
  },
  { 
    projectTypeId: 6, 
    active: false, 
    label: null,  
    projectScope: [
      ExtendedProjectType.DAMAGE_ASSESSMENT_RESTORATION, 
      ExtendedProjectType.RESTORATION
    ], 
    platforms: [
      ProjectOperationSource.PROJECT_MANAGER_APP
    ] 
  },
  { 
    projectTypeId: 7, 
    active: false, 
    label: null,  
    projectScope: [
      ExtendedProjectType.DAMAGE_ASSESSMENT_FIRE, 
      ExtendedProjectType.RESTORATION
    ], 
    platforms: [
      ProjectOperationSource.PROJECT_MANAGER_APP
    ] 
  },
  { 
    projectTypeId: 8, 
    active: false, 
    label: null,  
    projectScope: [
      ExtendedProjectType.DEVICE_ASSIGNMENT_DRYING
    ], 
    platforms: [
      ProjectOperationSource.PORTAL, 
      ProjectOperationSource.DEVICE_MANAGER_APP
    ] 
  },
  { 
    projectTypeId: 9, 
    active: false, 
    label: null,  
    projectScope: [
      ExtendedProjectType.DEVICE_ASSIGNMENT_LENDING
    ], 
    platforms: [
      ProjectOperationSource.PORTAL, 
      ProjectOperationSource.DEVICE_MANAGER_APP
    ] 
  },
];

type ParentKey = string;
export type InfoConfigInput = Record<ParentKey, string[]>;

export interface Room {
  id: string;
  contactPersonId: string;
  roomNumber: number;
  numWalls: number;
  infoInput?: InfoConfigInput;
  /** Written by IRES Project during a drying extension; its `infoInput` replaces the room's own. */
  dryingExtensionData?: { infoInput?: InfoConfigInput };
}

export interface ProjectDetails {
  id: string;
  clientData: ClientData;
  projectConclusionData?: ProjectConclusionData;

  leakDetectionConclusionData?: LeakDetectionConclusionData;

  damagePatternData?: DamagePatternData;
  finishDamagePatternData?: FinishDamagePatternData;
  documentationAndLeakDetectionData?: DocumentationAndLeakDetectionData;

  internalProjectNumber: number;
  firmShort: string;
  panelAssignmentDates?: object;
  deviceAssignmentDates?: object;
  syncTarget?: ProjectSyncTarget;
  projectImportSource?: ProjectImportSource;
  setupDryingData?: SetupDryingData;
  dryingProgressData?: DryingProgressData;
  packupDevicesData?: PackupDevicesData;
  createdWithAppVersion?: number;
  /**
   * `projectScope` is deprecated and should not be used. Please see `ClientData.extendedProjectScope`.
   */
  projectScope?: ProjectType[];
  /**
   * `visibilityRestrictions` is deprecated and should not be used. Please see `Project.extendedVisibilityRestrictions`.
   */
  visibilityRestrictions?: VisibilityRestriction[];
  extendedVisibilityRestrictions?: ExtendedVisibilityRestriction[];

  projectOwners?: object;
  projectSupervisorId?: string;

  projectOwnerStaffNumber?: string;
  projectSupervisorStaffNumber?: string;

  pdfDocument?: string | null;
  pdfDocument2?: string | null;

  lastOperation?: ProjectLastOperation;
  lastTimeChanged?: RecordInfo;

  setupDevicesData?: SetupDevicesData;
  conclusion?: ProjectConclusion | null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tradesData?: any;
  testProject?: boolean;
}

export interface SetupDryingData {
  complete: boolean | null;
}

export interface DryingProgressData {
  complete: boolean | null;
}

export interface PackupDevicesData {
  complete: boolean | null;
}

export interface ProjectSyncTarget {
  syncTargetIds: string[];
}

export interface ProjectImportSource {
  sourceId: string;
}

export function isIdOfFlexProject(projectId: string) {
  return projectId.endsWith("_flex");
}

export function isFlexProject(
  projectDetails: ProjectDetails | ProjectOverview
) {
  return isIdOfFlexProject(projectDetails.id);
}

export function getProjectScope(project: ProjectDetails | ProjectOverview) {
  const projectDetails = project as ProjectDetails;

  if (projectDetails) {
    return (
      projectDetails.projectScope ?? projectDetails.clientData?.projectScope
    );
  } else {
    const overview = project as ProjectOverview;

    if (overview) {
      return overview.projectScope;
    }
  }
}

export function isPortalOnlyProjectTypeId(projectTypeId: number) {
  const found = ALL_PROJECT_TYPES.find(p => p.projectTypeId === projectTypeId);

  if (!found) {
    return false;
  }

  return found.platforms.includes(ProjectOperationSource.PORTAL) && !found.platforms.includes(ProjectOperationSource.PROJECT_MANAGER_APP);
}

export function isDryingOnlyProjectTypeId(projectTypeId: number) {
  const found = ALL_PROJECT_TYPES.find(p => p.projectTypeId === projectTypeId);

  if (!found) {
    return false;
  }

  return found.projectScope.includes(ExtendedProjectType.DRYING) && !found.projectScope.includes(ExtendedProjectType.LEAK_DETECTION);
}

export function isLeakDetectionOnlyProjectTypeId(projectTypeId: number) {
  const found = ALL_PROJECT_TYPES.find(p => p.projectTypeId === projectTypeId);

  if (!found) {
    return false;
  }

  return found.projectScope.includes(ExtendedProjectType.LEAK_DETECTION) && !found.projectScope.includes(ExtendedProjectType.DRYING);
}

export function isRepairOnlyProjectTypeId(projectTypeId: number) {
  const found = ALL_PROJECT_TYPES.find(p => p.projectTypeId === projectTypeId);

  if (!found) {
    return false;
  }

  return found.projectScope.includes(ExtendedProjectType.RESTORATION) && 
    !found.projectScope.includes(ExtendedProjectType.LEAK_DETECTION) &&
    !found.projectScope.includes(ExtendedProjectType.DRYING);
}

export function doesProjectTypeIdHaveLeakDetection(projectTypeId: number) {
  const found = ALL_PROJECT_TYPES.find(p => p.projectTypeId === projectTypeId);

  if (!found) {
    return false;
  }

  return found.projectScope.includes(ExtendedProjectType.LEAK_DETECTION);
}

export function doesProjectTypeIdHaveDrying(projectTypeId: number) {
  const found = ALL_PROJECT_TYPES.find(p => p.projectTypeId === projectTypeId);

  if (!found) {
    return false;
  }

  const scope = found.projectScope;

  return scope.includes(ExtendedProjectType.DRYING) || scope.includes(ExtendedProjectType.DEVICE_ASSIGNMENT_DRYING);
}

export function doesProjectTypeIdHaveDamageAssessment(projectTypeId: number) {
  const found = ALL_PROJECT_TYPES.find(p => p.projectTypeId === projectTypeId);

  if (!found) {
    return false;
  }

  const scope = found.projectScope;

  return scope.includes(ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING) || 
    scope.includes(ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING_RESTORATION) ||
    scope.includes(ExtendedProjectType.DAMAGE_ASSESSMENT_FIRE) ||
    scope.includes(ExtendedProjectType.DAMAGE_ASSESSMENT_RESTORATION);
}

export function doesProjectTypeIdHaveRestoration(projectTypeId: number) {
  const found = ALL_PROJECT_TYPES.find(p => p.projectTypeId === projectTypeId);

  if (!found) {
    return false;
  }

  const scope = found.projectScope;

  return scope.includes(ExtendedProjectType.RESTORATION);
}

export function isOldLeakDetectionProject(
  projectDetails: ProjectDetails | ProjectOverview
) {
  return projectDetails.id.endsWith("_ld");
}

export function containsDryingInfo(
  projectDetails: ProjectDetails | ProjectOverview
) {
  const projectTypeId = (projectDetails as ProjectOverview).projectTypeId ?? (projectDetails as ProjectDetails).clientData?.projectTypeId;

  if (projectTypeId) {
    return doesProjectTypeIdHaveDrying(projectTypeId);
  }

  if (isFlexProject(projectDetails)) {
    const projectScope = getProjectScope(projectDetails);

    if (projectScope) {
      return projectScope.some((value) => ProjectType.DRYING === value);
    }
  }

  return !isOldLeakDetectionProject(projectDetails);
}

export function containsLeakDetectionInfo(
  projectDetails: ProjectDetails | ProjectOverview
) {
  const projectTypeId = (projectDetails as ProjectOverview).projectTypeId ?? (projectDetails as ProjectDetails).clientData?.projectTypeId;

  if (projectTypeId) {
    return doesProjectTypeIdHaveLeakDetection(projectTypeId);
  }

  if (isFlexProject(projectDetails)) {
    const projectScope = getProjectScope(projectDetails);

    if (projectScope) {
      return projectScope.some((value) => ProjectType.LEAK_DETECTION === value);
    }
  }

  return isOldLeakDetectionProject(projectDetails);
}

export function containsOnlyLeakDetectionInfo(
  projectDetails: ProjectDetails | ProjectOverview
) {
  const projectTypeId = (projectDetails as ProjectOverview).projectTypeId ?? (projectDetails as ProjectDetails).clientData?.projectTypeId;

  if (projectTypeId) {
    return isLeakDetectionOnlyProjectTypeId(projectTypeId);
  }

  const projectScope = getProjectScope(projectDetails);

  if (projectScope && Array.isArray(projectScope) && !projectScope.includes(ProjectType.DRYING)) {
    return true;
  }

  return false;
}

export function formatProjectScope(
  vue: Vue,
  projecScope: ExtendedProjectType
) {
  switch (projecScope) {
    case ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING:
    case ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING_RESTORATION:
    case ExtendedProjectType.DAMAGE_ASSESSMENT_FIRE:
    case ExtendedProjectType.DAMAGE_ASSESSMENT_RESTORATION:
      return vue.$t("projectDetails.projectScope.damageAssessment").toString();
    case ExtendedProjectType.DEVICE_ASSIGNMENT_DRYING:
      return vue.$t("projectDetails.projectScope.deviceAssignment").toString();
    case ExtendedProjectType.DEVICE_ASSIGNMENT_LENDING:
      return vue.$t("projectDetails.projectScope.devicesRental").toString();
    case ExtendedProjectType.DRYING:
      return vue.$t("projectDetails.projectScope.drying").toString();
    case ExtendedProjectType.LEAK_DETECTION:
      return vue.$t("projectDetails.projectScope.leakDetection").toString();
    case ExtendedProjectType.RESTORATION:
      return vue.$t("projectDetails.projectScope.restoration").toString();

    default:
      return vue.$t("common.unknown").toString();
  }
}

export interface LeakDetectionConclusionData {
  concludedBy: RecordInfo | null;
}

export interface ProjectConclusionData {
  startedBy?: RecordInfo | null;
  finishedBy: RecordInfo | null;
  projectConclusionDate: null | firestore.Timestamp;
  dryingSuccessful?: null | boolean;
  dryingSuccessfulSetBy?: RecordInfo;
  repairAction?: null | RepairAction;
  repairCompletedBy?: RecordInfo;

  pdfDocuments?: string[];
  complete?: boolean;
  didConfirmTimeTrackingInput?: boolean;
  recordInfo?: RecordInfo | null;
}

export interface FinishDamagePatternData {
  complete?: boolean;
  completedBy?: RecordInfo | null;
  concludedBy?: RecordInfo | null;
  leakDetectionCompletedBy?: RecordInfo | null;
  startedBy?: RecordInfo;
  conclusionState?: DamagePatternConclusionState | null;
  conclusionStateLastChangedBy?: RecordInfo | null;
  estimatedDryingDurationInDays?: number;
  /** Drying surface area of the floor, in m² with at most 2 decimals (captured at drying release, step 9). */
  dryingAreaFloor?: number;
  /** Drying surface area of walls/ceiling, in m² with at most 2 decimals (captured at drying release, step 9). */
  dryingAreaWallCeiling?: number;
  damagePatternComplete?: boolean;
  lastChangedBy?: RecordInfo;

  /**
   * `projectState` is deprecated and should not be used. Please see `Project.conclusion`.
   */
  projectState?: ProjectConclusionState | null;
}

export interface DocumentationAndLeakDetectionData {
  completedBy?: RecordInfo | null;
  startedBy?: RecordInfo;
  pdfDocuments?: string[];
}

export interface DamagePatternData {
  complete?: boolean | null;
  recordInfo?: RecordInfo | null;
  startedBy?: RecordInfo;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apartmentInfo?: any[];
  rooms?: Room[];
  /** The Beschreibung version the project's answers were recorded with, e.g. "5.03". */
  roomInfoConfigVersion?: string;
}

export interface ContactInfo {
  phone?: string;
  mobilePhone?: string;
  emailAddress?: string;
}

export interface LocationInfo {
  countryCode?: string;
  zipCode?: string;
  city?: string;
  street?: string;
  houseNumber?: string;
  /**
   * Position of this address: geocoded by IRES Cloud on write, or placed by hand on the
   * map, which is recorded as quality `MANUAL` and then never overwritten automatically.
   *
   * Only a project's `damageLocationInfo` writes it today. `GeneralInfo` and `AddressInfo`
   * inherit the field so the policy-holder and billing addresses can adopt it later
   * without another model change.
   */
  coordinates?: GeoLocation | null;
}

export interface NameInfo {
  title?: string;
  firstName?: string;
  lastName?: string;
}

export interface GeneralInfo extends NameInfo, LocationInfo, ContactInfo {}

export interface ContactPersonInfo extends NameInfo, ContactInfo {
  id: string;
  contactPersonNumber: number;
  houseNumber?: string;
  floor?: string;
  note?: string;
  lastEditedBy?: RecordInfo;
}

export interface ApartmentForElectricityBillingInfo {
  apartmentNumber: number;
  emailAddress?: string;
  id: string;
  name: string;
}

export interface SetupDevicesData {
  apartmentsForElectricityBilling?: Array<ApartmentForElectricityBillingInfo>;
  complete?: boolean;
  latestAssignmentCrteatedBy?: RecordInfo;
  recordInfo?: RecordInfo;
  startedBy?: RecordInfo;
}

export interface AddressInfo extends NameInfo, LocationInfo, ContactInfo {}

export interface RegulatorInfo extends NameInfo, ContactInfo {
  regulatorType: RegulatorType | null;
}

export enum ProjectNameSource {
  USER_INPUT = "USER_INPUT",
  CONTACT_PERSON = "CONTACT_PERSON",
  PROJECT_NAME_IRES = "PROJECT_NAME_IRES",
  PROJECT_NAME_OFFICE = "PROJECT_NAME_OFFICE",
}

/**
 * Extended project name source enum that supports additional values not available in
 * the legacy `ProjectNameSource` enum. Follows the `projectScope` / `extendedProjectScope` pattern.
 * When reading, prefer `extendedProjectNameSource` and fall back to `projectNameSource`.
 */
export enum ExtendedProjectNameSource {
  USER_INPUT = "USER_INPUT",
  CONTACT_PERSON = "CONTACT_PERSON",
  PROJECT_NAME_IRES = "PROJECT_NAME_IRES",
  PROJECT_NAME_OFFICE = "PROJECT_NAME_OFFICE",
  DAMAGE_LOCATION = "DAMAGE_LOCATION",
  TEMPLATE = "TEMPLATE",
}

export enum RegulatorType {
  VU_FIELD_SALES = "VU_FIELD_SALES",
  VU_INTERNAL_SALES = "VU_INTERNAL_SALES",
  EXTERNAL_REGULATOR = "EXTERNAL_REGULATOR",
  AGENT = "AGENT",
  VU_AGENCY = "VU_AGENCY",
}

export enum ApprovalResult {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  DELETED = "DELETED",
}

export enum RepairAction {
  NOT_NEEDED = "NOT_NEEDED",
  IMMEDIATELY = "IMMEDIATELY",
  APPOINTMENT = "APPOINTMENT",
}

export interface ProjectApprovalData {
  recordInfo: RecordInfo;
  approvalResult: ApprovalResult;
}

export interface CostCentre {
  name: string;
  projectType: string;
}

export interface Contact extends NameInfo, ContactInfo {
  floor?: string;
  note?: string;
}

export interface ClientData {
  costCentres?: CostCentre[];
  /**
   * `projectScope` is deprecated and should not be used. Please see `ClientData.extendedProjectScope`.
   */
  projectScope: ProjectType[];
  extendedProjectScope?: ExtendedProjectType[];
  projectTypeId?: number;
  projectApproval: ProjectApprovalData | null;
  projectStartDate: firestore.Timestamp;
  contractDate: firestore.Timestamp | null;
  policyHolderInfo: GeneralInfo;
  projectManagerId?: string;
  lossAdjusterId?: string;
  directOrder: boolean | null;
  projectNumber: string;
  projectNameErp?: string;
  projectNumberIres?: string;
  note: string;
  complete?: boolean;
  startedBy?: RecordInfo;
  branch: string | null;
  recordInfo: RecordInfo;

  contact?: Contact;

  plannedDamageAssessmentDate?: firestore.Timestamp;
  plannedDamageAssessmentTime?: firestore.Timestamp;

  firstContactDate: firestore.Timestamp | null;
  firstContactTime: firestore.Timestamp | null;
  firstContactBy?: string;

  leakDetectionNecessary?: boolean;

  leakDetectionTechnician?: string;
  leakDetectionDate: firestore.Timestamp | null;
  plannedLeakDetectionDate: firestore.Timestamp | null;

  insuranceName?: string;
  additionalInsuranceInfo?: string;
  insuranceContactName?: string;
  insurancePhone?: string;
  insuranceMobile?: string;
  insuranceEmail?: string;
  policyNumber?: string;
  eventNumber?: string;
  damageNumber: string;
  damageId?: string | null;
  damageDay: firestore.Timestamp | null;
  damageType?: string;
  contractType?: string;

  damageLocationInfo: LocationInfo;
  contactPersons: Array<ContactPersonInfo>;

  regulatorInfo: RegulatorInfo;
  projectName?: string;
  projectNameSource: ProjectNameSource;
  /**
   * Extended project name source with additional values (`DAMAGE_LOCATION`, `TEMPLATE`).
   * Prefer this over `projectNameSource` when available; fall back to `projectNameSource` for backward compatibility.
   */
  extendedProjectNameSource?: ExtendedProjectNameSource;

  /**
   * `projectState` is deprecated and should not be used. Please see `Project.conclusion`.
   */
  projectState?: ProjectConclusionState | null;
}

export function getProjectOwnerId(projectDetails: ProjectDetails) {
  const projectOwners = projectDetails.projectOwners;

  if (!projectOwners) {
    return null;
  }

  for (const [userId, isCurrentProjectOwner] of Object.entries(projectOwners)) {
    if (isCurrentProjectOwner) {
      return userId;
    }
  }

  return null;
}

export function isPortalOnlyProject(project: ProjectDetails | ProjectOverview) {
  return (project.visibilityRestrictions != undefined &&
    project.visibilityRestrictions.includes(VisibilityRestriction.WEBSITE_ONLY)) ||
    (project.extendedVisibilityRestrictions != undefined &&
    project.extendedVisibilityRestrictions.includes(ExtendedVisibilityRestriction.WEBSITE_ONLY));
}

export function isProjectWebsiteOnly(projectDetails: ProjectDetails) {
  return isPortalOnlyProject(projectDetails);
}

export function formatProjectNameAndNumber(
  projectName: string | undefined | null,
  projectNumber: string | undefined | null,
  separator?: string
): string {
  if (projectName == projectNumber) {
    return projectName ?? "";
  }

  return joinToString([projectNumber, projectName], separator ?? ", ");
}

/**
 * Maps electricity-billing apartments onto {@link ContactPersonInfo} so they can be rendered and
 * matched alongside the project's real contact persons.
 *
 * Two details are load-bearing and must not be "simplified" away:
 *
 * - The `id` carries the {@link USED_FOR_ELECTRICITY_BILLING_PREFIX}, matching the
 *   `contactPersonId` the project app writes onto device assignments. This is what makes
 *   {@link isOnlyUsedForElectricityBilling} work, so these entries render as "Strombezug N"
 *   instead of being mistaken for an apartment.
 * - The `contactPersonNumber` is derived from the persisted `apartmentNumber` (minus
 *   {@link ELECTRICITY_BILLING_APARTMENT_NUMBER_OFFSET}), never from the number of real contact
 *   persons. Numbering off the contact-person count made the label shift whenever a contact
 *   person was added or removed, and collide with real apartment numbers.
 *
 * @param {ApartmentForElectricityBillingInfo[]} apartmentsForElectricityBilling - The project's
 *        electricity-billing apartments (`setupDevicesData.apartmentsForElectricityBilling`).
 * @returns {ContactPersonInfo[]} One synthetic contact person per apartment. These are display
 *          projections only and are never persisted to `clientData.contactPersons`.
 */
export function mapApartmentsForElectricityBillingToContactPersons(
  apartmentsForElectricityBilling: ApartmentForElectricityBillingInfo[],
): ContactPersonInfo[] {
  return apartmentsForElectricityBilling.map((apartment, index) => ({
    id: USED_FOR_ELECTRICITY_BILLING_PREFIX + apartment.id,
    // Older entries (and the app's model default) may carry an un-offset number; fall back to the
    // position so the label stays a small positive number instead of going negative.
    contactPersonNumber:
      apartment.apartmentNumber > ELECTRICITY_BILLING_APARTMENT_NUMBER_OFFSET
        ? apartment.apartmentNumber - ELECTRICITY_BILLING_APARTMENT_NUMBER_OFFSET
        : index + 1,
    lastName: apartment.name,
    emailAddress: apartment.emailAddress,
  }));
}

/**
 * Removes the electricity-billing prefix from a device assignment's `contactPersonId`, so a
 * prefixed and an unprefixed id for the same apartment compare equal.
 *
 * Apply this to **both** sides of a comparison: assignments written by the project app carry the
 * prefix, while ones written by older portal code and the real contact persons do not.
 *
 * @param {string} contactPersonId - A contact person id from either side of the comparison.
 * @returns {string} The id without the electricity-billing prefix.
 */
export function stripElectricityBillingPrefix(contactPersonId: string): string {
  return contactPersonId.startsWith(USED_FOR_ELECTRICITY_BILLING_PREFIX)
    ? contactPersonId.slice(USED_FOR_ELECTRICITY_BILLING_PREFIX.length)
    : contactPersonId;
}

function getAllowedPlatformsFor(projectManagementType: ProjectManagement) {
   switch (projectManagementType) {
    case ProjectManagement.DEVICES_ONLY_WEBSITE:
    case ProjectManagement.WEBSITE:
      return [ProjectOperationSource.PORTAL];
    case ProjectManagement.PROJECT_OPTION:
      return [ProjectOperationSource.PORTAL, ProjectOperationSource.PROJECT_MANAGER_APP];
    case ProjectManagement.DEFAULT:
      return [ProjectOperationSource.PROJECT_MANAGER_APP];
    
    default:
      return [ProjectOperationSource.PORTAL, ProjectOperationSource.PROJECT_MANAGER_APP];
  }
}

export function getProjectTypeCode(
  vue: Vue,
  projectTypeId: number,
  projectTypes?: ProjectTypeConfig[]
): string {
  const list = projectTypes ?? ALL_PROJECT_TYPES;
  const found = list.find(p => p.projectTypeId === projectTypeId);
  const raw = (found && found.typeBadge) ? found.typeBadge : null;
  if (raw) {
    if (raw === '--') return raw;
    const cleaned = raw.toString().replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 2);
    if (cleaned.length > 0) return cleaned;
  }
  return vue.$t(`projectDetails.projectTypeCodes.${projectTypeId}`).toString();
}

export function formatProjectTypeName(
  vue: Vue, 
  projectTypeId: number,
  projectTypes?: ProjectTypeConfig[]
): string {
  const found = (projectTypes ?? ALL_PROJECT_TYPES).find(p => p.projectTypeId === projectTypeId);
  return found?.label && found.label !== "" ? found.label : vue.$t(`projectDetails.projectTypeDefaultLables.${projectTypeId}`).toString();
}

/**
 * Returns the legacy "Leak Detection" project type id.
 *
 * Definition (legacy):
 *  - MUST include {@link ExtendedProjectType.LEAK_DETECTION}
 *  - MUST NOT include {@link ExtendedProjectType.DRYING}
 *  - MUST be available in {@link ProjectOperationSource.PROJECT_MANAGER_APP}
 *
 * Looks up {@link ALL_PROJECT_TYPES} and returns the matching {@code projectTypeId}.
 * Returns {@code 0} if no matching legacy type is configured.
 *
 * @returns {number} The project type id for the legacy "Leak Detection" type, or 0 if not found.
 */
export function getLegacyLeakDetectionProjectTypeId() {
  const found = ALL_PROJECT_TYPES.find(p => 
    p.projectScope.includes(ExtendedProjectType.LEAK_DETECTION) && 
    !p.projectScope.includes(ExtendedProjectType.DRYING) &&
    p.platforms.includes(ProjectOperationSource.PROJECT_MANAGER_APP)
  );

  if (!found) {
    return 0;
  }

  return found.projectTypeId;
}

/**
 * Returns the legacy "Drying" project type id.
 *
 * Definition (legacy):
 *  - MUST NOT include {@link ExtendedProjectType.LEAK_DETECTION}
 *  - MUST include {@link ExtendedProjectType.DRYING}
 *  - MUST include {@link ExtendedProjectType.RESTORATION}
 *  - MUST include {@link ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING_RESTORATION}
 *  - MUST be available in {@link ProjectOperationSource.PROJECT_MANAGER_APP}
 *
 * Looks up {@link ALL_PROJECT_TYPES} and returns the matching {@code projectTypeId}.
 * Returns {@code 0} if no matching legacy type is configured.
 *
 * @returns {number} The project type id for the legacy "Drying" type, or 0 if not found.
 */
export function getLegacyDryingProjectTypeId() {
  const found = ALL_PROJECT_TYPES.find(p => 
    !p.projectScope.includes(ExtendedProjectType.LEAK_DETECTION) && 
    p.projectScope.includes(ExtendedProjectType.DRYING) &&
    p.projectScope.includes(ExtendedProjectType.RESTORATION) &&
    p.projectScope.includes(ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING_RESTORATION) &&
    p.platforms.includes(ProjectOperationSource.PROJECT_MANAGER_APP)
  );

  if (!found) {
    return 0;
  }

  return found.projectTypeId;
}

/**
 * Returns the legacy "Leak Detection + Drying" project type id.
 *
 * Definition (legacy):
 *  - MUST include {@link ExtendedProjectType.LEAK_DETECTION}
 *  - MUST include {@link ExtendedProjectType.DRYING}
 *  - MUST include {@link ExtendedProjectType.RESTORATION}
 *  - MUST include {@link ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING_RESTORATION}
 *  - MUST be available in {@link ProjectOperationSource.PROJECT_MANAGER_APP}
 *
 * Looks up {@link ALL_PROJECT_TYPES} and returns the matching {@code projectTypeId}.
 * Returns {@code 0} if no matching legacy type is configured.
 *
 * @returns {number} The project type id for the legacy "Leak Detection + Drying" type, or 0 if not found.
 */
export function getLegacyLeakDetectionAndDryingProjectTypeId() {
  const found = ALL_PROJECT_TYPES.find(p => 
    p.projectScope.includes(ExtendedProjectType.LEAK_DETECTION) && 
    p.projectScope.includes(ExtendedProjectType.DRYING) &&
    p.projectScope.includes(ExtendedProjectType.RESTORATION) &&
    p.projectScope.includes(ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING_RESTORATION) &&
    p.platforms.includes(ProjectOperationSource.PROJECT_MANAGER_APP)
  );

  if (!found) {
    return 0;
  }

  return found.projectTypeId;
}

/**
 * Returns the legacy "Portal-only (Device Assignment – Drying)" project type id.
 *
 * Definition (legacy):
 *  - MUST include {@link ExtendedProjectType.DEVICE_ASSIGNMENT_DRYING}
 *  - MUST be available in {@link ProjectOperationSource.PORTAL}
 *
 * Looks up {@link ALL_PROJECT_TYPES} and returns the matching {@code projectTypeId}.
 * Returns {@code 0} if no matching legacy type is configured.
 *
 * @returns {number} The project type id for the legacy portal-only type, or 0 if not found.
 */
export function getLegacyPortalOnlyProjectTypeId() {
  const found = ALL_PROJECT_TYPES.find(p => 
    p.projectScope.includes(ExtendedProjectType.DEVICE_ASSIGNMENT_DRYING) &&
    p.platforms.includes(ProjectOperationSource.PORTAL)
  );

  if (!found) {
    return 0;
  }

  return found.projectTypeId;
}

/**
 * Resolves the effective {@code projectTypeId} for a project.
 *
 * Resolution order:
 *  1) Use the explicit {@code projectTypeId} if present on {@link ProjectOverview} or {@link ProjectDetails.clientData}.
 *  2) Else, if the project is portal-only (via legacy or extended visibility), return the legacy portal-only type id.
 *  3) Else, infer from content flags:
 *     - If contains both leak detection and drying info → legacy "Leak Detection + Drying" type id.
 *     - Else if contains leak detection only → legacy "Leak Detection" type id.
 *     - Else if contains drying only → legacy "Drying" type id.
 *  4) Fallback to {@code 0} if nothing matches.
 *
 * Notes:
 *  - This function provides a safe mapping for legacy records that may not have {@code projectTypeId} set yet.
 *  - Uses {@link containsLeakDetectionInfo} and {@link containsDryingInfo} as feature proxies when inferring.
 *
 * @param {ProjectDetails | ProjectOverview} project The project record (details or overview).
 * @returns {number} The resolved project type id, or 0 when it cannot be determined.
 */
export function getProjectTypeIdOfProject(project: ProjectDetails | ProjectOverview) {
  let projectTypeId = 0;

  const hasDrying = containsDryingInfo(project);
  const hasLeakDetection = containsLeakDetectionInfo(project);

  const projectTypeId_ = (project as ProjectOverview).projectTypeId ?? (project as ProjectDetails).clientData?.projectTypeId;

  if (projectTypeId_) {
    projectTypeId = projectTypeId_;
  }
  else if (isPortalOnlyProject(project)) {
    projectTypeId = getLegacyPortalOnlyProjectTypeId();
  }
  else if (hasDrying && hasLeakDetection) {
    projectTypeId = getLegacyLeakDetectionAndDryingProjectTypeId();
  }
  else if (hasLeakDetection) {
    projectTypeId = getLegacyLeakDetectionProjectTypeId();
  }
  else if (hasDrying) {
    projectTypeId = getLegacyDryingProjectTypeId();
  }

  return projectTypeId;
}

export function getProjectEndDateLegacy(project: ProjectDetails) {
  let endDate: firestore.Timestamp | null = null;

  if (!project) {
    return null;
  }

  const isLeakDetectionProject = getIsLeakDetectionProject(project.id);

  if (isLeakDetectionProject) {
    if (project.leakDetectionConclusionData?.concludedBy) {
      endDate = project.leakDetectionConclusionData.concludedBy.recordedAt;
    }
  } else {
    if (
      project.clientData?.projectApproval?.approvalResult ===
      ApprovalResult.REJECTED
    ) {
      endDate = project.clientData.projectApproval.recordInfo.recordedAt;
    } else if (project.projectConclusionData?.projectConclusionDate) {
      if (project.projectConclusionData?.complete) {
        endDate = project.projectConclusionData.projectConclusionDate;
      } else if (
        project.finishDamagePatternData?.conclusionState ===
        DamagePatternConclusionState.NO_DRYING_NECESSARY
      ) {
        endDate = project.projectConclusionData.projectConclusionDate;
      } else if (
        project.finishDamagePatternData?.conclusionState ===
        DamagePatternConclusionState.NO_CONTRACT
      ) {
        endDate = project.projectConclusionData.projectConclusionDate;
      }
      /**
       * `finishDamagePatternData.projectState` is deprecated and should not be used. Please see `Project.conclusion`.
       */ 
      else if (
        project.finishDamagePatternData?.projectState &&
        project.finishDamagePatternData.projectState !== null
      ) {
        endDate = project.projectConclusionData.projectConclusionDate;
      }
      /**
       * `clientData.projectState` is deprecated and should not be used. Please see `Project.conclusion`.
       */  
      else if (
        project.clientData?.projectState &&
        project.clientData.projectState !== null
      ) {
        endDate = project.projectConclusionData.projectConclusionDate;
      }
    } else {
      const projectTypeId = getProjectTypeIdOfProject(project);
      
      if (isLeakDetectionOnlyProjectTypeId(projectTypeId)) {
        const leakDetectionEnd =
          project?.finishDamagePatternData?.leakDetectionCompletedBy
            ?.recordedAt;

        if (leakDetectionEnd &&
          (
            !project.createdWithAppVersion || 
            project.createdWithAppVersion < 1655
          )
        ) {
          endDate = leakDetectionEnd;
        }
        else if (
          project.finishDamagePatternData?.damagePatternComplete &&
          project.finishDamagePatternData.completedBy?.recordedAt &&
          project.createdWithAppVersion &&
          project.createdWithAppVersion >= 1655
        ) {
          endDate = project.finishDamagePatternData.completedBy?.recordedAt;
        }
      }
    }
  }

  return endDate;
}

/**
 * Resolves a project's end date, extending `getProjectEndDateLegacy` with the `conclusion` data.
 *
 * Every conclusion state counts, `RESTORED` included: restoring a project stamps
 * `conclusion = { conclusionState: RESTORED, conclusionDate: now }`, and a restored project is meant
 * to land in the finished projects list and stay there. Do not exclude `RESTORED` here. `isProjectFinished`
 * is built on this function and also drives running-vs-finished overview routing, so excluding it would
 * migrate restored projects into the running list. See `docs/projects/archived-projects-list.md`.
 *
 * Kept in sync with the Cloud counterpart in `ProjectsRepository.getProjectEndDate`.
 */
export function getProjectEndDate(project: ProjectDetails) {
  let endDate = getProjectEndDateLegacy(project);
  const projectConclusion = project.conclusion;

  if (
    projectConclusion && 
    projectConclusion !== null
  ) {
    const projectConclusionDate = projectConclusion.conclusionDate;
    
    if (projectConclusionDate) {
      endDate = projectConclusionDate;
    }
  }
  return endDate;
}

export function isProjectFinished(project: ProjectDetails) {
  return getProjectEndDate(project) ? true : false;
}

export function formatProjectOperationSource(
  vue: Vue,
  projectOperationSource: ProjectOperationSource
) {
  switch (projectOperationSource) {
    case ProjectOperationSource.CLOUD_FUNCTION:
      return vue.$t("projectOperationSource.cloudFunction").toString();
    case ProjectOperationSource.ERP:
    case ProjectOperationSource.API:
      return vue.$t("projectOperationSource.api").toString();
    case ProjectOperationSource.PORTAL:
      return vue.$t("projectOperationSource.portal").toString();
    case ProjectOperationSource.PROJECT_MANAGER_APP:
      return vue.$t("projectOperationSource.projectManagerApp").toString();
    case ProjectOperationSource.DEVICE_MANAGER_APP:
      return vue.$t("projectOperationSource.deviceManagerApp").toString();
    case ProjectOperationSource.PYTHON_SCRIPT:
      return vue.$t("projectOperationSource.pythonScript").toString();

    default:
      return "";
  }
}

/**
 * Whether a contact person id identifies an electricity-billing apartment ("Strombezug") rather
 * than a real contact person. Use this for a device assignment's `contactPersonId`; for a
 * {@link ContactPersonInfo} use {@link isOnlyUsedForElectricityBilling} instead.
 *
 * @param {string} contactPersonId - The contact person id to test.
 * @returns {boolean} `true` when the id carries the electricity-billing prefix.
 */
export function isElectricityBillingContactPersonId(contactPersonId: string) {
  return contactPersonId.startsWith(USED_FOR_ELECTRICITY_BILLING_PREFIX);
}

export function isOnlyUsedForElectricityBilling(
  contactPersonInfo: ContactPersonInfo
) {
  return isElectricityBillingContactPersonId(contactPersonInfo.id);
}

/**
 * Computes the default list of project types for the current client configuration.
 *
 * Logic overview:
 *  1) Reads the current {@link ProjectManagement} via {@link getProjectManagementType}.
 *  2) Creates a deep copy of {@link ALL_PROJECT_TYPES} and filters it to entries whose
 *     {@link ProjectTypeConfig.platforms} intersect with the allowed platforms derived
 *     from the project management type (see {@link getAllowedPlatformsFor}).
 *  3) Determines activation defaults:
 *     - Project App modes (DEFAULT, PROJECT_OPTION): activates ids resolved by
 *       {@link getLegacyLeakDetectionProjectTypeId}, {@link getLegacyDryingProjectTypeId},
 *       {@link getLegacyLeakDetectionAndDryingProjectTypeId}.
 *     - Portal modes (WEBSITE, DEVICES_ONLY_WEBSITE, PROJECT_OPTION): activates the id
 *       resolved by {@link getLegacyPortalOnlyProjectTypeId}.
 *  4) In PROJECT_MANAGER-only mode (DEFAULT), removes the PORTAL platform from each type’s
 *     {@link ProjectTypeConfig.platforms} to avoid exposing portal-only surfaces.
 *
 * Returns a new array (no mutation of constants) with computed `active` flags and
 * (potentially) pruned `platforms` per type.
 *
 * @returns {ProjectTypeConfig[]} Default project types adjusted for management mode,
 *          with `active` flags and valid `platforms`.
 */
export function getDefaultProjectTypes() {
  const projectManagementType = getProjectManagementType();
  const base: ProjectTypeConfig[] = JSON.parse(JSON.stringify(ALL_PROJECT_TYPES));
  const allowedPlatforms = new Set(getAllowedPlatformsFor(projectManagementType));

  // Filter by platform intersection
  let filtered = base.filter(pt => pt.platforms.some(p => allowedPlatforms.has(p)));

  const isProjectAppOnly = projectManagementType === ProjectManagement.DEFAULT;
  const isProjectApp = projectManagementType === ProjectManagement.PROJECT_OPTION || projectManagementType === ProjectManagement.DEFAULT;
  const isPortal = projectManagementType === ProjectManagement.WEBSITE || projectManagementType === ProjectManagement.DEVICES_ONLY_WEBSITE || projectManagementType === ProjectManagement.PROJECT_OPTION;

  const activateIdsForProjectApp = new Set([
    getLegacyLeakDetectionProjectTypeId(), 
    getLegacyDryingProjectTypeId(),
    getLegacyLeakDetectionAndDryingProjectTypeId()
  ]);
  const activateIdsForPortal = new Set([
    getLegacyPortalOnlyProjectTypeId()
  ]);
    
  filtered = filtered.map(pt => {
    const active =
      (isProjectApp && activateIdsForProjectApp.has(pt.projectTypeId)) ||
      (isPortal && activateIdsForPortal.has(pt.projectTypeId)) ||
      false;
    return { ...pt, active };
  });

  filtered = filtered.map(pt => {
    const active =
      (isProjectApp && activateIdsForProjectApp.has(pt.projectTypeId)) ||
      (isPortal && activateIdsForPortal.has(pt.projectTypeId)) ||
      false;

    const platforms =
      pt.platforms.filter((platform) => !(isProjectAppOnly && platform === ProjectOperationSource.PORTAL));
    
    return { ...pt, active, platforms};
  });

  return filtered;
}